import {downloadFile, nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {sendGaPageView} from '../../util/analytics';
import {
    RankPositionFilter,
    SearchRankingSort,
    SubscriptionModuleName,
    SubscriptionModuleStatus
} from '../../util/enums';
import {onSetCrumbs} from '../../util/events';
import {
    ActivateHandle,
    OrderDirection,
    SearchRankingProduct,
    SearchRankingStats,
    TrackedKeyword
} from '../../util/interfaces';
import {
    addSearchRankingKeyword,
    exportSearchRankings,
    getManualKeywordsCount,
    getSearchRankingColumns,
    getSearchRankingProducts,
    getSearchRankingStats,
    saveSearchRankingColumns,
    SearchRankingFilters
} from '../../util/requestSender';
import {PortalSelectOption, SearchRankingMode} from '../../util/types';
import {AppContext} from '../misc/appContext';
import AddKeywordsController from './addKeywords/addKeywordsController';
import SearchRankings from './searchRankings';
import {getAllColumns, getDefaultVisibleFields, getVisibleColumns} from './searchRankingsColumns';
import {SUBSCRIPTION_ROUTE} from '../../util/constants';
import {arraysAreEqual, createErrorMessage} from '../../util/utils';
import {useNavigate} from 'react-router';

const DEFAULT_LIMIT = 25;
const PREFERENCE_SAVE_DEBOUNCE_MS = 1000;

type LoadParams = {page?: number};

const emptyStats: SearchRankingStats = {
    position_counts: {
        [RankPositionFilter.ALL]: 0,
        [RankPositionFilter.TOP_1]: 0,
        [RankPositionFilter.TOP_3]: 0,
        [RankPositionFilter.TOP_5]: 0,
        [RankPositionFilter.TOP_10]: 0,
        [RankPositionFilter.TOP_30]: 0,
        [RankPositionFilter.BOTTOM_100]: 0
    },
    avg_rank: 0,
    best_rank: 0,
    total_keywords: 0,
    total_categories: 0,
    total_search_volume: 0,
    visibility_score: 0
};

const UNSUB_MANUAL_KEYWORDS_LIMIT = 100;
const SUB_MANUAL_KEYWORDS_LIMIT = 10_000;

export default function SearchRankingsController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account, accounts} = context.accountContext;
    const {t} = useTranslation();
    const navigate = useNavigate();

    // Data
    const [products, setProducts] = useState<Array<SearchRankingProduct>>([]);
    const [stats, setStats] = useState<SearchRankingStats>(emptyStats);
    const [prevStats, setPrevStats] = useState<SearchRankingStats>(emptyStats);

    // Loading
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);

    // Limit
    const [manualKeywordsCount, setManualKeywordsCount] = useState(0);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [rankingMode, setRankingMode] = useState<SearchRankingMode>('best');
    const [activePositionFilter, setActivePositionFilter] = useState<RankPositionFilter>(RankPositionFilter.ALL);
    const [selectedCountries, setSelectedCountries] = useState<Array<'NL' | 'BE'>>(['NL']);
    const [selectedAccountIDs, setSelectedAccountIDs] = useState<Array<string>>(account ? [account.id] : []);
    const accountsHaveChanges = useRef(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Table
    const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
    const [sortField, setSortField] = useState<SearchRankingSort | null>(null);
    const [sortOrder, setSortOrder] = useState<OrderDirection>(OrderDirection.ASC);
    const [page, setPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    // Column + filter preferences
    const [visibleFields, setVisibleFields] = useState<Array<string>>(() => getDefaultVisibleFields(t));
    const [columnsLoaded, setColumnsLoaded] = useState(false);
    const preferenceSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [hasModule, manualKeywordsLimit] = useMemo(() => {
        const hasModule =
            user?.subscription?.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS, [
                SubscriptionModuleStatus.TRIAL
            ]) ?? false;
        const limit = hasModule ? SUB_MANUAL_KEYWORDS_LIMIT : UNSUB_MANUAL_KEYWORDS_LIMIT;
        return [hasModule, limit];
    }, [user?.id, user?.subscription?.modules]);

    const columns = useMemo(() => getVisibleColumns(visibleFields, t), [visibleFields, t]);

    const allColumnItems: Array<PortalSelectOption<string>> = useMemo(
        () => getAllColumns(t).map(col => ({value: col.field, label: col.label})),
        [t]
    );

    // Import dialog
    const [importDialogActive, setImportDialogActive] = useState(false);

    // Request ID counters to ignore stale API responses
    const statsRequestId = useRef(0);
    const productsRequestId = useRef(0);

    // Breadcrumbs + analytics (mount only)
    useEffect(() => {
        onSetCrumbs([{label: t('searchRankings.crumb', 'Search Rankings')}]);
        sendGaPageView('search-rankings');
    }, []);

    // Load column + filter preferences when account changes
    useEffect(() => {
        if (!account) return;
        getSearchRankingColumns({
            data: {account_id: account.id},
            callback: (data: ResponseData) => {
                setColumnsLoaded(true);
                if (data.status !== 'success') return;

                // Restore columns
                if (data.columns && Array.isArray(data.columns) && data.columns.length > 0) {
                    setVisibleFields(data.columns);
                }

                // Restore filters
                const filters = data.filters as SearchRankingFilters | undefined;
                if (filters) {
                    if (filters.ranking_mode === 'average' || filters.ranking_mode === 'best') {
                        setRankingMode(filters.ranking_mode);
                    }
                    if (filters.country_codes) {
                        setSelectedCountries(filters.country_codes);
                    }
                    if (filters.account_ids && Array.isArray(filters.account_ids) && filters.account_ids.length) {
                        setSelectedAccountIDs(filters.account_ids);
                    }
                }
            }
        });
    }, [account]);

    // Stats + all-products effect reacts only to filter changes (NOT page/limit/sort)
    useEffect(() => {
        loadStats();
    }, [account, selectedCountries, searchQuery, activePositionFilter, refreshTrigger, columnsLoaded]);

    // Products effect reacts to all changes including page/limit/sort
    useEffect(() => {
        loadTableData();
    }, [
        account,
        selectedCountries,
        rankingMode,
        searchQuery,
        activePositionFilter,
        sortField,
        sortOrder,
        page,
        limit,
        refreshTrigger,
        columnsLoaded
    ]);

    function onCloseAccountItems() {
        if (!accountsHaveChanges.current) return;
        setPage(0);
        setRefreshTrigger(current => current + 1);
        savePreferences();
    }

    function loadStats() {
        if (!account || !columnsLoaded || !selectedAccountIDs.length) {
            if (!account) setLoadingStats(false);
            return;
        }

        const accountId = account.id;
        setLoadingStats(true);
        const reqId = ++statsRequestId.current;

        // Fetch stats, includes comparison
        getSearchRankingStats({
            data: {
                account_id: accountId,
                country_codes: selectedCountries,
                account_ids: selectedAccountIDs,
                search: nullify(searchQuery),
                position_filter: activePositionFilter
            },
            callback: (data: ResponseData) => {
                if (reqId !== statsRequestId.current) return;
                setLoadingStats(false);
                if (data.success) {
                    setStats(data.stats);
                    setPrevStats(data.comparison);
                } else {
                    setStats(emptyStats);
                    setPrevStats(emptyStats);
                }
            }
        });
    }

    function loadTableData(params?: LoadParams) {
        if (!account || !columnsLoaded || !selectedAccountIDs.length) {
            if (!account) setLoadingProducts(false);
            return;
        }

        setLoadingProducts(true);
        // setProducts([]);
        const reqId = ++productsRequestId.current;

        getSearchRankingProducts({
            data: {
                account_id: account.id,
                country_codes: selectedCountries,
                account_ids: selectedAccountIDs,
                ranking_mode: rankingMode,
                search: nullify(searchQuery),
                position_filter: activePositionFilter,
                sort_field: sortField,
                sort_order: sortOrder,
                page: params?.page ?? page,
                limit
            },
            callback: (data: ResponseData) => {
                if (reqId !== productsRequestId.current) return;
                setLoadingProducts(false);
                if (data.success) {
                    const {products, total} = data;
                    setProducts(products);
                    if (total > -1) setTotalRows(total);
                    accountsHaveChanges.current = false;
                } else {
                    setProducts([]);
                    setTotalRows(0);
                }
            }
        });
    }

    // Ref to hold latest filter/column values for debounced save
    const latestVisibleFields = useRef(visibleFields);
    const latestRankingMode = useRef(rankingMode);
    const latestCountry = useRef(selectedCountries);
    const latestAccountIDs = useRef(selectedAccountIDs);
    const latestAccount = useRef(account);

    // Keep refs in sync
    useEffect(() => {
        latestVisibleFields.current = visibleFields;
    }, [visibleFields]);
    useEffect(() => {
        latestRankingMode.current = rankingMode;
    }, [rankingMode]);
    useEffect(() => {
        latestCountry.current = selectedCountries;
    }, [selectedCountries]);
    useEffect(() => {
        if (selectedAccountIDs.length) latestAccountIDs.current = selectedAccountIDs;
    }, [selectedAccountIDs]);
    useEffect(() => {
        latestAccount.current = account;
    }, [account]);

    useEffect(() => {
        if (!account) return;
        const accountId = account.id;
        getManualKeywordsCount({
            account_id: accountId,
            callback: (data: ResponseData) => {
                if (!data.success) return;
                if (typeof data.count !== 'number') return;
                setManualKeywordsCount(data.count);
            }
        });
    }, [account?.id, refreshTrigger]);

    function onConfirmUpgradeLimit() {
        navigate(SUBSCRIPTION_ROUTE);
        // TODO Confirm if we only want to navigate instead of activating the module. If so, delete commented code.
        // const subscription = user?.subscription;
        // if (!subscription || !account || !manualKeywordsLimit) return;
        // subscription.addModule(
        //     {accountID: account.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
        //     (subscription: Subscription, success: boolean, data: ResponseData) => {
        //         if (success) {
        //             user.subscription = subscription;
        //             context.onChangeContext({user: user.clone()});
        //             showSnackbar(
        //                 t('searchRankings.upgradeLimit.snackbarAdded', 'De module is toegevoegd aan het abonnement')
        //             );
        //         } else
        //             showDialog(
        //                 createErrorMessage(
        //                     data.message ??
        //                     t(
        //                         'searchRankings.upgradeLimit.errorMessage',
        //                         'Het toevoegen van de module is niet gelukt.'
        //                     )
        //                 ),
        //                 t('searchRankings.upgradeLimit.errorTitle', 'Toevoegen mislukt')
        //             );
        //     }
        // );
    }

    function onUpgradeLimit() {
        if (hasModule) return;
        showDialog(
            <p style={{whiteSpace: 'pre-line'}}>
                {t(
                    'searchRankings.upgradeLimit.message',
                    'Upgrade om extra zoektermen te kunnen tracken.\n\nVoeg Bolmate Premium toe in jouw abonnement en ga van 100 naar 10.000 zoektermen.'
                )}
            </p>,
            t('searchRankings.upgradeLimit.title', 'Bolmate Premium'),
            [
                {label: t('common.cancel', 'Annuleren'), primary: false},
                {
                    label: t('searchRankings.upgradeLimit.confirm', 'Naar abonnement gaan'),
                    onClick: onConfirmUpgradeLimit
                }
            ]
        );
    }

    // Debounced save of all preferences (columns + filters)
    const savePreferences = useCallback(() => {
        if (preferenceSaveTimer.current) clearTimeout(preferenceSaveTimer.current);
        const acc = latestAccount.current;
        if (!acc) return;

        preferenceSaveTimer.current = setTimeout(() => {
            saveSearchRankingColumns({
                data: {
                    account_id: acc.id,
                    columns: latestVisibleFields.current,
                    filters: {
                        ranking_mode: latestRankingMode.current,
                        country_codes: latestCountry.current,
                        account_ids: latestAccountIDs.current
                    }
                },
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(t('searchRankings.snackbar.preferencesSaved', 'Voorkeuren opgeslagen'));
                    }
                }
            });
        }, PREFERENCE_SAVE_DEBOUNCE_MS);
    }, [t]);

    // Column preference change handler
    const handleColumnsChange = useCallback(
        (_values: string | null, _selected: boolean, values?: Array<string>) => {
            if (values) setVisibleFields(values);
            savePreferences();
        },
        [savePreferences]
    );

    // Flush pending save on unmount (instead of just clearing the timer)
    useEffect(() => {
        return () => {
            if (preferenceSaveTimer.current) {
                clearTimeout(preferenceSaveTimer.current);
                const acc = latestAccount.current;
                if (acc) {
                    saveSearchRankingColumns({
                        data: {
                            account_id: acc.id,
                            columns: latestVisibleFields.current,
                            filters: {
                                ranking_mode: latestRankingMode.current,
                                country_codes: latestCountry.current,
                                account_ids: latestAccountIDs.current
                            }
                        },
                        callback: () => {}
                    });
                }
            }
        };
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
        setPage(0);
    }, []);

    const handleCountryChange = useCallback(
        (value: string) => {
            setSelectedCountries(value.split(',') as Array<'NL' | 'BE'>);
            setPage(0);
            savePreferences();
        },
        [savePreferences]
    );

    function onAccountChange(_value: string | null, _selected: boolean, values: Array<string>) {
        if (!accountsHaveChanges.current) accountsHaveChanges.current = !arraysAreEqual(selectedAccountIDs, values);
        setSelectedAccountIDs(values);
    }

    const handleRankingModeChange = useCallback(
        (mode: SearchRankingMode) => {
            setRankingMode(mode);
            setPage(0);
            savePreferences();
        },
        [savePreferences]
    );

    const handlePositionFilterChange = useCallback((filter: RankPositionFilter) => {
        setActivePositionFilter(filter);
        setPage(0);
    }, []);

    const handleSort = useCallback((field: SearchRankingSort) => {
        setSortField(prev => {
            if (prev === field) {
                setSortOrder(o => (o === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC));
                return prev;
            }
            setSortOrder(OrderDirection.ASC);
            return field;
        });
        setPage(0);
    }, []);

    const handleToggleExpand = useCallback((ean: string) => {
        setExpandedProductId(prev => (prev === ean ? null : ean));
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleLimitChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(0);
    }, []);

    const handleImportClick = useCallback(() => {
        setImportDialogActive(true);
    }, []);

    const handleImportClose = useCallback(() => {
        setImportDialogActive(false);
    }, []);

    // Add keyword dialog (lifted to top-level for proper overlay stacking)
    const addKeywordsRef = useRef<ActivateHandle>(null);

    const handleKeywordsChanged = useCallback(() => {
        setRefreshTrigger((prev: number) => prev + 1);
    }, []);

    const onAddKeyword = useCallback(
        (keyword: string, ean: string, callback: (keyword: TrackedKeyword, restored: boolean) => void) => {
            if (!account) return;
            addSearchRankingKeyword({
                data: {ean, keyword, account_id: account.id},
                callback: (data: ResponseData) => {
                    if (data.success) {
                        const addedKeyword: TrackedKeyword = data.keyword;
                        handleKeywordsChanged();
                        showSnackbar(
                            t('searchRankings.snackbar.keywordAdded', 'Keyword "{{keyword}}" toegevoegd', {
                                keyword: addedKeyword.keyword
                            })
                        );
                        callback(addedKeyword, data.restored ?? false);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'searchRankings.dialog.addKeywordFailedMessage',
                                        'Het toevoegen van het keyword is niet gelukt.'
                                    )
                            ),
                            t('searchRankings.dialog.addKeywordFailedTitle', 'Toevoegen mislukt')
                        );
                }
            });
        },
        [account?.id, t, handleKeywordsChanged]
    );

    const accountItems: Array<PortalSelectOption> = useMemo(
        () => accounts.map(acc => ({value: acc.id, label: acc.labelWithSellerID})),
        [accounts]
    );

    // Default: select all stores when accounts load (and no saved preference)
    const storesInitialized = useRef(false);
    useEffect(() => {
        if (account && !storesInitialized.current) {
            storesInitialized.current = true;
            // Only set default if no stores are selected yet (no saved preference loaded)
            setSelectedAccountIDs([account.id]);
        }
    }, [account]);

    const accountId = account?.id ?? '';

    const handleExportExcel = useCallback(() => {
        if (!account || !selectedAccountIDs.length) {
            if (!account) setLoadingProducts(false);
            return;
        }
        const accountId = account.id;
        setLoadingProducts(true);
        exportSearchRankings({
            data: {
                account_id: accountId,
                country_codes: selectedCountries,
                account_ids: selectedAccountIDs,
                ranking_mode: rankingMode,
                search: nullify(searchQuery),
                position_filter: activePositionFilter,
                sort_field: sortField,
                sort_order: sortOrder,
                page: -1, // No-op
                limit: -1 // No-op
            },
            callback: (data: ResponseData, response: Response) => {
                if (data.success) {
                    response.blob().then((blob: Blob) => {
                        const header = response.headers.get('Content-Disposition');
                        let fileName = 'search-rankings-export.xlsx';
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setLoadingProducts(false);
                    });
                } else {
                    setLoadingProducts(false);
                    showDialog(data.message ?? t('exportError', 'Fout bij het exporteren naar Excel.'));
                }
            }
        });
    }, [
        accountId,
        selectedCountries,
        selectedAccountIDs,
        rankingMode,
        searchQuery,
        activePositionFilter,
        visibleFields,
        accountItems,
        t
    ]);

    return (
        <>
            <SearchRankings
                products={products}
                stats={stats}
                prevStats={prevStats}
                loadingProducts={loadingProducts}
                loadingStats={loadingStats}
                onSearchChange={handleSearchChange}
                rankingMode={rankingMode}
                onRankingModeChange={handleRankingModeChange}
                activePositionFilter={activePositionFilter}
                onPositionFilterChange={handlePositionFilterChange}
                selectedCountries={selectedCountries}
                onCountryChange={handleCountryChange}
                selectedAccountIDs={selectedAccountIDs}
                storeItems={accountItems}
                onStoreChange={onAccountChange}
                expandedProductId={expandedProductId}
                onToggleExpand={handleToggleExpand}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                page={page}
                limit={limit}
                totalRows={totalRows}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                accountId={accountId}
                onKeywordsChanged={handleKeywordsChanged}
                columns={columns}
                onExportExcel={handleExportExcel}
                visibleColumnFields={visibleFields}
                allColumnItems={allColumnItems}
                onColumnsChange={handleColumnsChange}
                importDialogActive={importDialogActive}
                onImportClick={handleImportClick}
                onImportClose={handleImportClose}
                onAddKeyword={onAddKeyword}
                manualKeywordsCount={manualKeywordsCount}
                manualKeywordsLimit={manualKeywordsLimit}
                onUpgradeLimit={onUpgradeLimit}
                hasModule={hasModule}
                onCloseAccountItems={onCloseAccountItems}
            />
            <AddKeywordsController
                ref={addKeywordsRef}
                onSuccess={handleKeywordsChanged}
                onOpenImport={handleImportClick}
            />
        </>
    );
}
